import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import SEO from '@/components/SEO';
import SeoBreadcrumbs from '@/components/SeoBreadcrumbs';
import HowToSchema from '@/components/HowToSchema';
import { ArrowRight, Ruler, CheckCircle } from 'lucide-react';

const STEPS = [
  { name: 'Measure the perimeter', text: 'Walk the outside of the property and measure each external wall in order, so the lengths form a closed loop when you add them back up.' },
  { name: 'Note the wall thickness', text: 'Record how thick each wall is (and where its centreline sits relative to what you measured) — this affects the true internal room size, not just the overall footprint.' },
  { name: 'Add the internal partitions', text: 'Measure each internal wall the same way, room by room, including any partition that splits one room from another.' },
  { name: 'Mark doors and windows', text: 'For each opening, record its width and its position along the wall (the distance from the nearest corner works well).' },
  { name: 'Check the plan closes', text: 'Add up the wall lengths around each room and around the whole building. If the numbers do not add up to a closed shape, re-check the measurement that looks wrong before moving on.' },
  { name: 'Calculate room extents', text: 'Once every wall is in place, the internal area of each room — and the Gross Internal Area (GIA) of the whole property — can be read directly off the finished drawing.' },
  { name: 'Present the finished plan', text: 'Add room names, dimensions and a scale bar, then lay the plan out for its intended use — a working drawing, a lease plan, or a marketing floor plan.' },
];

const HowToDrawFloorPlanFromMeasurements = () => {
  const breadcrumbs = [
    { name: "Home", path: "/" },
    { name: "Floor Plan Creator", path: "/floor-plan-creator" },
    { name: "How to Draw a Floor Plan From Measurements", path: "/guides/how-to-draw-a-floor-plan-from-measurements" }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEO
        pageId="guide-draw-from-measurements"
        defaultTitle="Draw a Floor Plan from Measurements | House Plan Direct"
        defaultDescription="A step-by-step guide to drawing an accurate floor plan from your own measurements — perimeter, wall thickness, partitions, doors and windows, and checking the plan closes."
        aiSummary="To draw a floor plan from measurements: measure the external perimeter, note wall thickness, measure internal partitions, mark door and window positions, check the measurements form a closed shape, then calculate room extents and Gross Internal Area (GIA). The House Plan Direct Floor Plan Creator lets you enter each of these measurements directly and draws the plan to scale automatically."
        keywords="how to draw a floor plan from measurements, how to create a floor plan from measurements, draw floor plan to scale, floor plan measurements guide"
      />
      <HowToSchema
        steps={STEPS}
        name="How to Draw a Floor Plan From Measurements"
        description="Step-by-step guide to drawing an accurate floor plan from your own measurements."
        url="https://houseplandirect.com/guides/how-to-draw-a-floor-plan-from-measurements"
      />
      <Header />
      <SeoBreadcrumbs items={breadcrumbs} />
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-emerald-50 to-white">
        <div className="max-w-3xl mx-auto text-center">
          <div className="flex items-center justify-center mb-4">
            <Ruler className="h-12 w-12 text-emerald-600" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6">
            How to Draw a Floor Plan From Measurements
          </h1>
          <p className="text-xl text-gray-600 mb-8">
            Seven steps to turn a tape-measure survey into an accurate, to-scale floor plan.
          </p>
          <Link to="/floor-plan-creator">
            <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700" data-testid="guide-measurements-link-creator">
              Open the Floor Plan Creator <ArrowRight className="ml-2 h-5 w-5" />
            </Button>
          </Link>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <p className="text-gray-600 mb-10">
            You don't need CAD experience to draw an accurate floor plan — just a tape measure (or laser
            measure) and a methodical approach. Work through the steps below in order, entering each
            measurement into the <Link to="/floor-plan-creator" className="text-emerald-700 underline hover:text-emerald-800">Floor Plan Creator</Link> as
            you go, and it will draw the plan to scale for you automatically.
          </p>

          <ol className="space-y-6 mb-12">
            {STEPS.map((step, i) => (
              <li key={step.name} className="flex gap-4" data-testid={`guide-measurements-step-${i + 1}`}>
                <div className="w-9 h-9 rounded-full bg-emerald-600 text-white flex items-center justify-center font-bold flex-shrink-0">{i + 1}</div>
                <div>
                  <h2 className="font-semibold text-lg text-gray-900 mb-1">{step.name}</h2>
                  <p className="text-gray-600">{step.text}</p>
                </div>
              </li>
            ))}
          </ol>

          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-8 mb-12">
            <h2 className="text-xl font-semibold text-gray-900 mb-4">Why the plan needs to "close"</h2>
            <p className="text-gray-700">
              A floor plan is a closed loop of walls. If you measure every external wall and the shape
              doesn't come back to where it started, one of the measurements is wrong (or a corner isn't a
              right angle). Catching this early — before you've added rooms, doors and windows — saves a lot
              of rework, which is why it's worth checking the perimeter closes before adding internal detail.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><CheckCircle className="h-5 w-5 text-emerald-600" /> Already have a plan to check?</h3>
              <p className="text-sm text-gray-600 mb-3">If you're verifying an existing PDF or image rather than measuring from scratch, use Plan Measure instead — calibrate the drawing to one known dimension and measure directly from it.</p>
              <Link to="/measurement-tool" className="text-emerald-700 underline hover:text-emerald-800 text-sm font-medium" data-testid="guide-measurements-link-measure">Measure an existing floor plan online →</Link>
            </div>
            <div className="bg-gray-50 p-6 rounded-xl">
              <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2"><CheckCircle className="h-5 w-5 text-emerald-600" /> Building a marketing floor plan?</h3>
              <p className="text-sm text-gray-600 mb-3">Once your walls and rooms are drawn, use the Present workspace to lay the same plan out with room names, areas and GIA for a property listing.</p>
              <Link to="/estate-agent-floor-plans" className="text-emerald-700 underline hover:text-emerald-800 text-sm font-medium">See estate agent floor plans →</Link>
            </div>
          </div>

          <div className="bg-emerald-600 text-white rounded-xl p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">Ready to draw your floor plan?</h2>
            <p className="text-emerald-100 mb-6">Free to create and edit. Unlock the finished project for £2.99.</p>
            <Link to="/floor-plan-creator">
              <Button size="lg" variant="secondary" className="bg-white text-emerald-600 hover:bg-gray-100">
                Start Creating Free <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default HowToDrawFloorPlanFromMeasurements;
